import React from 'react';
import { Plus } from 'lucide-react';
import { formatDisplayHours } from '../../../../utils/employeeTimeEntryHours';
import { useModuleAccess } from '../../../../hooks/useModuleAccess';
import { IstWeekList, LeadIstHoursOverviewModal } from './LeadIstWeeks';
import { entriesForActivity, entriesWithoutActivity, formatIstLine, groupIstByWeek, isImportedTimeEntry, istEntryBadge, totalWorkHours, type LeadProjectTimeEntry, type LeadTimeActivityOption } from './projectActivityIstHours';

export type { LeadProjectTimeEntry };

export type LeadProjectTimeData = {
  items: LeadProjectTimeEntry[];
  activities: LeadTimeActivityOption[];
  reload: () => void;
};

const TIME_ENTRIES_CHANGED_EVENT = 'lead-time-entries-changed';

/** Tells every view of this project (appointment list, controlling) to reload its Ist times. */
export function notifyLeadTimeEntriesChanged(leadId: string): void {
  window.dispatchEvent(new CustomEvent(TIME_ENTRIES_CHANGED_EVENT, { detail: { leadId } }));
}

/** Runs `onChange` whenever Ist times of this project were changed elsewhere on the page. */
export function useLeadTimeEntriesChanged(leadId: string, onChange: () => void): void {
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  React.useEffect(() => {
    const handler = (e: Event) => {
      if ((e as CustomEvent<{ leadId?: string }>).detail?.leadId === leadId) onChangeRef.current();
    };
    window.addEventListener(TIME_ENTRIES_CHANGED_EVENT, handler);
    return () => window.removeEventListener(TIME_ENTRIES_CHANGED_EVENT, handler);
  }, [leadId]);
}

export function useLeadProjectTimeEntries(leadId: string): LeadProjectTimeData {
  const [items, setItems] = React.useState<LeadProjectTimeEntry[]>([]);
  const [activities, setActivities] = React.useState<LeadTimeActivityOption[]>([]);
  const [version, setVersion] = React.useState(0);
  // Ist-Zeiten kommen aus der Zeiterfassung (Modul Mitarbeiter).
  const { loaded: modulesLoaded, isEnabled } = useModuleAccess();
  const hrEnabled = modulesLoaded && isEnabled('hr');

  React.useEffect(() => {
    if (!hrEnabled) {
      setItems([]);
      setActivities([]);
      return;
    }
    let cancelled = false;
    fetch(`/api/leads/${leadId}/time-entries`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : { items: [], activities: [] }))
      .then((data) => {
        if (cancelled) return;
        setItems(Array.isArray(data?.items) ? data.items : []);
        setActivities(Array.isArray(data?.activities) ? data.activities : []);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [leadId, version, hrEnabled]);

  const reload = React.useCallback(() => setVersion((v) => v + 1), []);
  useLeadTimeEntriesChanged(leadId, reload);
  return { items, activities, reload };
}

function IstLine({
  row,
  onEdit,
}: {
  row: LeadProjectTimeEntry;
  onEdit?: (row: LeadProjectTimeEntry) => void;
}) {
  const badge = istEntryBadge(row);
  const content = (
    <>
      {formatIstLine(row)}
      {badge ? (
        <span className="ml-1.5 rounded bg-app-surface-muted px-1 py-px text-[10px] font-medium text-app-text-subtle">
          {badge}
        </span>
      ) : null}
    </>
  );
  if (!onEdit) return <p className="text-xs text-app-text-muted">{content}</p>;
  if (isImportedTimeEntry(row)) {
    return (
      <p className="text-xs text-app-text-muted" title="Aus MeinSHK übernommen – Änderungen bitte dort vornehmen.">
        {content}
      </p>
    );
  }
  return (
    <button
      type="button"
      // Appointment cards are clickable themselves; keep the click on this line.
      onClick={(e) => {
        e.stopPropagation();
        onEdit(row);
      }}
      onKeyDown={(e) => e.stopPropagation()}
      className="block text-left text-xs text-app-text-muted hover:text-app-text hover:underline"
      title="Ist-Zeit bearbeiten"
    >
      {content}
    </button>
  );
}

/** Soll/Ist line on a planned or done appointment card. Hidden when this Termin has no stamps and no edit right. */
export function ActivityClockedHours({
  activityId,
  occurrenceIds,
  items,
  onEdit,
  onAdd,
}: {
  activityId: string;
  occurrenceIds?: string[];
  items: LeadProjectTimeEntry[];
  /** Set when the user may edit Ist times. */
  onEdit?: (row: LeadProjectTimeEntry) => void;
  onAdd?: () => void;
}) {
  const rows = entriesForActivity(items, activityId, occurrenceIds);
  if (rows.length === 0 && !onAdd) return null;

  return (
    <div className="mt-0.5 space-y-0.5">
      {rows.map((row) => (
        <IstLine key={row.id} row={row} onEdit={onEdit} />
      ))}
      {onAdd ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onAdd();
          }}
          onKeyDown={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-xs font-medium text-app-text-subtle hover:text-pipedrive-blue"
        >
          <Plus className="h-3 w-3" />
          Ist-Zeit
        </button>
      ) : null}
    </div>
  );
}

/** Project time booked without an appointment (e.g. rework). New entries are added from Controlling. */
export function ProjectTimeWithoutActivity({
  items,
  onEdit,
  sectionLabelClassName,
}: {
  items: LeadProjectTimeEntry[];
  onEdit?: (row: LeadProjectTimeEntry) => void;
  sectionLabelClassName: string;
}) {
  const rows = React.useMemo(() => entriesWithoutActivity(items), [items]);
  const weeks = React.useMemo(() => groupIstByWeek(rows), [rows]);
  const [showAll, setShowAll] = React.useState(false);
  if (rows.length === 0) return null;

  const visible = weeks.slice(0, RECENT_WEEKS);
  return (
    <div className="mb-4">
      <div className="my-6 flex items-baseline justify-between gap-2">
        <h3 className={sectionLabelClassName}>Projektzeiten ohne Termin</h3>
        <span className="text-xs tabular-nums text-app-text-subtle">
          {formatDisplayHours(totalWorkHours(rows))} h
        </span>
      </div>
      <div className="rounded-lg border border-app-border bg-app-surface px-3">
        <IstWeekList weeks={visible} onEdit={onEdit} />
        {weeks.length > visible.length ? (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="w-full border-t border-app-border py-2 text-left text-xs font-medium text-app-text-subtle hover:text-pipedrive-blue"
          >
            Alle {weeks.length} Wochen anzeigen
          </button>
        ) : null}
      </div>
      <LeadIstHoursOverviewModal
        open={showAll}
        onClose={() => setShowAll(false)}
        items={items}
        onEdit={onEdit}
        initialFilter="project"
      />
    </div>
  );
}

const RECENT_WEEKS = 4;
